import React, { useEffect, useState, useRef } from 'react';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useNavigate } from "react-router-dom";
import DatosDialog from "./components/dialogs/DatosDialog.js";
import DeudaDialog from "./components/dialogs/DeudaDialog.js";
import VentaDialog from "./components/dialogs/VentaDialog.js";
import TicketsDialog from "./components/dialogs/TicketsDialog.js";
import PropuestasDialog from "./components/dialogs/PropuestasDialog.js";
import { getSincronizarCliente, getDatosCliente } from "../../service/AffariService.js";
import ClienteActions from "./components/ClienteActions.js";

const TableCliente = ({ data, tipoDocumento }) => {
    const navigate = useNavigate();
    const op = useRef(null);
    const toast = useRef(null);
    const [hoverData, setHoverData] = useState(null);
    const [loadingHover, setLoadingHover] = useState(false);
    const [isDatosDialogVisible, setIsDatosDialogVisible] = useState(false);
    const [isDeudaDialogVisible, setIsDeudaDialogVisible] = useState(false);
    const [isVentaDialogVisible, setIsVentaDialogVisible] = useState(false);
    const [isTicketsDialogVisible, setIsTicketsDialogVisible] = useState(false);
    const [isPropuestasDialogVisible, setIsPropuestasDialogVisible] = useState(false);
    const [codigo, setCodigo] = useState(null);
    const tableContainerRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [scrollHeight, setScrollHeight] = useState(null);
    const [syncingCodigo, setSyncingCodigo] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [visibleCount, setVisibleCount] = useState(10);
    const loadMoreRef = useRef(null);
    const notify = (message) => { if (toast.current) toast.current.show(message); };
    const textFilterTemplate = (options) => {return <InputText value={options.value || ''} onChange={(e) => options.filterApplyCallback(e.target.value)} className="p-inputtext-sm w-full" style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }} placeholder="Buscar" />;};

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMobile) {
            setVisibleCount(10);
        }
    }, [isMobile, data?.length]);

    useEffect(() => {
        if (!isMobile) return;
        const node = loadMoreRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setVisibleCount((prev) => Math.min(prev + 10, Array.isArray(data) ? data.length : 0));
                }
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [isMobile, data]);

    const selectdatos = (rowData) => {
        setIsDatosDialogVisible(true);
        setCodigo(rowData.codigo);
    }

    const selectdeuda = (rowData) => {
        setIsDeudaDialogVisible(true);
        setCodigo(rowData.codigo);
    }

    const selectventa = (rowData) => {
        setIsVentaDialogVisible(true);
        setCodigo(rowData.codigo);
    }

    const selecttickets = (rowData) => {
        setIsTicketsDialogVisible(true);
        setCodigo(rowData.codigo);
    }

    const handleSeleccionar = (rowData) => {
        if (!rowData?.almacen) {
            notify({ severity: 'warn', summary: 'Cliente sin centro', detail: 'El cliente seleccionado no puede operar (falta centro)' });
            return;
        }

        const codigoStr = String(rowData?.codigo ?? '').trim();
        const isCodigoAlfanumerico = codigoStr !== '' && !/^\d+$/.test(codigoStr);
        if (isCodigoAlfanumerico) {
            notify({ severity: 'warn', summary: 'Código inválido', detail: 'El código del cliente debe ser numérico para continuar.' });
            return;
        }

        const propuestas = rowData?.propuestas ?? 0;
        navigate(`/cotizacion?codigo=${rowData.codigo}&tipo=${tipoDocumento}&propuestas=${propuestas}`);
    }

    const sincronizarCliente = async (rowData) => {
        setSyncingCodigo(rowData.codigo);
        const index = data.indexOf(rowData) + 1;
        try {
            const response = await getSincronizarCliente(rowData.codigo, index);
            if (response?.codigo) {
                notify({ severity: 'success', summary: 'Sincronización', detail: `Cliente ${rowData.codigo} sincronizado con éxito.` });
            }
        }catch (error) {
            notify({ severity: 'error', summary: 'Error', detail: `Error al sincronizar cliente ${rowData.codigo}. No existe o no se pudo recuperar los datos.` });
        } finally {
            setSyncingCodigo(null);
        }
    }

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return;
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
    };

    const handleMouseEnter = (e, rowData) => {
        setLoadingHover(true);
        setHoverData(null);
        op.current.show(e);
        getDatosCliente(rowData.codigo)
            .then(setHoverData)
            .catch(console.error)
            .finally(() => setLoadingHover(false));
    };

    const handleMouseLeave = () => {
        if (op.current) {
            op.current.hide();
        }
    };

    useEffect(() => {
        const updateScroll = () => {
            if (!tableContainerRef.current) return;
            const rect = tableContainerRef.current.getBoundingClientRect();
            const available = window.innerHeight - rect.top - 80;
            if (rect.bottom > window.innerHeight && available > 0) {
                setIsScrollable(true);
                setScrollHeight(`${Math.floor(available)}px`);
            } else {
                setIsScrollable(false);
                setScrollHeight(null);
            }
        };

        updateScroll();
        window.addEventListener('resize', updateScroll);
        return () => window.removeEventListener('resize', updateScroll);
    }, [data?.length]);

    const getRowClassName = (rowData) => {
        let cssClass = "";
        const alertas = rowData?.alertas;
        if (Array.isArray(alertas) && alertas.length > 0) {
            const color = alertas[0]?.["background-color"];
            if (typeof color === "string" && color.trim() !== "") {
                cssClass = color.startsWith("bg-") ? color : `bg-${color}`;
            }
        }

        const codigoStr = String(rowData?.codigo ?? '').trim();
        const isCodigoAlfanumerico = codigoStr !== '' && !/^\d+$/.test(codigoStr);
        if (!rowData?.almacen || isCodigoAlfanumerico) {
            cssClass = 'bg-pink-100';
        }

        return `cursor-pointer hover:surface-hover ${cssClass}`;
    };

    const visibleData = Array.isArray(data) ? data.slice(0, visibleCount) : [];
    const tableData = Array.isArray(data)
        ? data.map((row, index) => ({ ...row, _rowKey: `${row.codigo ?? 'row'}-${index}` }))
        : [];

    const rowClassName = (rowData) => {
        const codigoStr = String(rowData?.codigo ?? '').trim();
        const isCodigoAlfanumerico = codigoStr !== '' && !/^\d+$/.test(codigoStr);
        return (!rowData?.almacen || isCodigoAlfanumerico) ? 'bg-pink-100' : '';
    };
    
    return (
        <>
            <Toast ref={toast} />
            <OverlayPanel 
                ref={op} 
                dismissable 
                style={{ width: '320px' }}
            >
                {loadingHover ? (
                    <div className="flex justify-content-center align-items-center p-2">
                        <i className="pi pi-spin pi-spinner text-primary text-xl"></i>
                    </div>
                ) : (
                    hoverData && (
                        <div className="flex flex-column gap-0">
                            <div className="surface-card p-2 border-round shadow-none">
                                <div className="text-500 font-medium text-xs mb-1 uppercase tracking-wide">Identificación</div>
                                <div className="grid grid-nogutter gap-1">
                                    <div className="col-12 text-xs"><span className="font-bold text-700">Código:</span> {hoverData.cliente?.codigo}</div>
                                    <div className="col-12 text-xs"><span className="font-bold text-700">RUT:</span> {hoverData.cliente?.rut}</div>
                                    <div className="col-12 text-xs"><span className="font-bold text-700">Razón Social:</span> {hoverData.cliente?.razonSocial}</div>
                                </div>
                            </div>
                            <div className="surface-card p-2 border-round shadow-none border-top-1 surface-border">
                                <div className="text-500 font-medium text-xs mb-1 uppercase tracking-wide">Financiera</div>
                                <div className="grid grid-nogutter gap-2">
                                    <div className="col-6 text-xs">
                                        <div className="text-500 text-xs">Estado</div>
                                        <span className={`font-bold ${hoverData.dato?.estado === 'ACTIVO' ? 'text-green-600' : 'text-orange-600'}`}>{hoverData.dato?.estado}</span>
                                    </div>
                                    <div className="col-6 text-xs">
                                        <div className="text-500 text-xs">Crédito</div>
                                        <span className="font-medium text-900">{formatCurrency(hoverData.dato?.credito)}</span>
                                    </div>
                                    <div className="col-6 text-xs">
                                        <div className="text-500 text-xs">Saldo Factura</div>
                                        <span className="font-medium text-900">{formatCurrency(hoverData.dato?.saldoFactura)}</span>
                                    </div>
                                    <div className="col-6 text-xs">
                                        <div className="text-500 text-xs">Saldo OC</div>
                                        <span className="font-medium text-900">{formatCurrency(hoverData.dato?.saldoOrden)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                )}
            </OverlayPanel>

            <div ref={tableContainerRef}>
                {isMobile ? (
                    <div className="flex flex-column gap-3">
                        {Array.isArray(data) && data.length > 0 ? (
                            <>
                                {visibleData.map((rowData, index) => (
                                    <div key={`${rowData.codigo ?? 'row'}-${index}`} className={`surface-card border-1 surface-border border-round p-3 shadow-1 ${getRowClassName(rowData)}`}>
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <div className="flex align-items-center gap-2">
                                                <span className="text-500 text-sm">#{index + 1}</span>
                                                <span className="font-bold text-900">{rowData.codigo}</span>
                                            </div>
                                            <div className="flex align-items-center gap-2">
                                                {rowData.propuestas > 0 ? (
                                                    <Button
                                                        icon="pi pi-clock"
                                                        label={`${rowData.propuestas}`}
                                                        className="p-button-rounded p-button-primary p-button-outlined p-button-sm"
                                                        tooltip={`${rowData.propuestas} propuestas`}
                                                        tooltipOptions={{ position: 'top' }}
                                                        onClick={() => { setIsPropuestasDialogVisible(true); setCodigo(rowData.codigo); }}
                                                    />
                                                ) : null}
                                                <ClienteActions 
                                                    rowData={rowData}
                                                    onSeleccionar={() => handleSeleccionar(rowData)}
                                                    onSelectDatos={selectdatos}
                                                    onSelectDeuda={selectdeuda}
                                                    onSelectTickets={selecttickets}
                                                    onSelectVenta={selectventa}
                                                    onSincronizarCliente={sincronizarCliente}
                                                    loadingHover={loadingHover}
                                                    syncingCodigo={syncingCodigo}
                                                    tipo={tipoDocumento}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-column gap-2">
                                            <div className="flex flex-column">
                                                <span className="text-600 text-sm">Razón Social</span>
                                                <span
                                                    className="font-medium text-900 cursor-pointer hover:underline"
                                                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                                    onClick={() => selectdatos(rowData)}
                                                    onMouseEnter={(e) => handleMouseEnter(e, rowData)}
                                                    onMouseLeave={handleMouseLeave}
                                                    title={rowData.razonSocial}
                                                >
                                                    {rowData.razonSocial}
                                                </span>
                                            </div>
                                            <div className="grid grid-nogutter gap-2">
                                                <div className="col-6">
                                                    <div className="text-600 text-sm">RUT</div>
                                                    <div className="text-900">{rowData.rut}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-600 text-sm">Lista Precio</div>
                                                    <div className="text-900">{rowData.listaPrecio}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-600 text-sm">Vendedor</div>
                                                    <div className="text-900">{rowData.vendedor}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-600 text-sm">Oficina</div>
                                                    <div className="text-900">{rowData.zona}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {visibleCount < (Array.isArray(data) ? data.length : 0) ? (
                                    <div ref={loadMoreRef} className="flex justify-content-center align-items-center p-3 text-600">Cargando más...</div>
                                ) : null}
                            </>
                        ) : (
                            <div className="surface-card border-1 surface-border border-round p-3 text-center text-600">No hay ningún registro que mostrar</div>
                        )}
                    </div>
                ) : (
                    <DataTable
                        value={tableData}
                        tableStyle={{ minWidth: "50rem" }}
                        size="small"
                        paginator
                        rows={20}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        dataKey="_rowKey"
                        filterDisplay="row"
                        className="p-datatable-sm border-1 surface-border border-round"
                        globalFilterFields={["cliente.razonSocial"]}
                        stripedRows
                        rowHover                              // resalta filas al hover
                        showGridlines                         // muestra líneas de cuadrícula
                        sortMode="multiple"                   // orden múltiple por columnas
                        removableSort                         // permite limpiar orden
                        resizableColumns                      // permitir redimensionar columnas
                        columnResizeMode="fit"                // ajuste de columnas al redimensionar
                        reorderableColumns                    // reordenar columnas arrastrando el header
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                        currentPageReportTemplate="{first} - {last} de {totalRecords}"
                        scrollable={isScrollable}
                        scrollHeight={isScrollable ? scrollHeight : undefined}
                        emptyMessage={<div className="p-4 text-center text-600">No hay ningún registro que mostrar</div>}
                        rowClassName={rowClassName}
                    >
                        <Column
                            columnKey="acciones"
                            header = ""
                            body={(rowData) => (
                                <div className="flex align-items-center justify-content-center gap-2">
                                    <ClienteActions 
                                        rowData={rowData}
                                        onSeleccionar={() => handleSeleccionar(rowData)}
                                        onSelectDatos={selectdatos}
                                        onSelectDeuda={selectdeuda}
                                        onSelectTickets={selecttickets}
                                        onSelectVenta={selectventa}
                                        onSincronizarCliente={sincronizarCliente}
                                        loadingHover={loadingHover}
                                        syncingCodigo={syncingCodigo}
                                        tipoDocumento={tipoDocumento}
                                    />
                                </div>
                            )}
                            className="col-acciones"
                            
                        />
                        {tipoDocumento !== 'nota-venta' && (
                            <Column 
                                columnKey="propuestas"
                                header="Propuestas"
                                field="propuestas" 
                                className="col-propuestas " 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                body={(rowData) => (rowData.propuestas > 0 && (
                                    <div className="flex align-items-center justify-content-center">
                                        <Button
                                            icon="pi pi-clock"
                                            className="p-button-rounded p-button-info p-button-sm "
                                            tooltip={`Ver propuestas`}
                                            tooltipOptions={{ position: 'top' }} 
                                            onClick={() => { setIsPropuestasDialogVisible(true); setCodigo(rowData.codigo); }}
                                        />
                                    </div>
                                )) || ''} 
                            />
                        )}
                        <Column 
                            columnKey="index"
                            header="#" 
                            body={(rowData, options) => options.rowIndex + 1} 
                            className="col-index" 
                            headerClassName="surface-border border-bottom-0 border-right-1" 
                            filterHeaderClassName=" surface-border border-top-0 border-right-1" 
                        />
                        <Column 
                            columnKey="codigo"
                            field="codigo" 
                            header="Código" 
                            className="col-codigo" 
                            sortable 
                            filter 
                            filterElement={textFilterTemplate} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 border-left-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 border-left-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                        />
                        <Column 
                            columnKey="rut"
                            field="rut" 
                            header="RUT" 
                            className="col-rut" 
                            sortable 
                            filter={tipoDocumento !== 'boleta'} 
                            filterElement={tipoDocumento !== 'boleta' ? textFilterTemplate : null} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                        />
                        <Column 
                            columnKey="razonSocial"
                            field="razonSocial" 
                            header="Razón Social" 
                            className="col-razon-social" 
                            sortable 
                            filter 
                            filterElement={textFilterTemplate} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                            body={(rowData) => (
                            <div 
                                className="font-medium cursor-pointer hover:underline white-space-nowrap overflow-hidden text-overflow-ellipsis" 
                                style={{ maxWidth: "230px" }} 
                                onClick={() => selectdatos(rowData)} 
                                onMouseEnter={(e) => handleMouseEnter(e, rowData)}
                                onMouseLeave={handleMouseLeave}
                                title={rowData.razonSocial}
                            >
                                {rowData.razonSocial}
                            </div>
                        )}/>
                        
                        <Column 
                            columnKey="listaPrecio"
                            field="listaPrecio" 
                            header="Lista Precio" 
                            className="col-lista-precio" 
                            sortable 
                            filter 
                            filterElement={textFilterTemplate} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                        />
                        
                        <Column 
                            columnKey="zona"
                            field="zona" 
                            header={tipoDocumento !== 'nota-venta' ? 'Oficina' : 'Zona'} 
                            className="col-oficina" 
                            sortable 
                            filter 
                            filterElement={textFilterTemplate} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                        />
                        <Column 
                            columnKey="vendedor"
                            field="vendedor" 
                            header="Vendedor" 
                            className="col-vendedor" 
                            sortable 
                            filter 
                            filterElement={textFilterTemplate} 
                            showFilterMenu={false} 
                            headerClassName="border-right-1 surface-border border-bottom-0" 
                            filterHeaderClassName="border-right-1 surface-border border-top-0" 
                            filterMatchMode="contains"
                        />
                    </DataTable>
                )}
            </div>

            <DatosDialog
                visible={isDatosDialogVisible}
                onCancel={() => { setIsDatosDialogVisible(false); }}
                codigo={codigo}
            />

            <DeudaDialog
                visible={isDeudaDialogVisible}
                onCancel={() => { setIsDeudaDialogVisible(false); }}
                codigo={codigo}
            />

            <VentaDialog
                visible={isVentaDialogVisible}
                onCancel={() => { setIsVentaDialogVisible(false); }}
                codigo={codigo}
            />

            <TicketsDialog
                visible={isTicketsDialogVisible}
                onCancel={() => { setIsTicketsDialogVisible(false); }}
                codigo={codigo}
            />

            <PropuestasDialog
                visible={isPropuestasDialogVisible}
                onCancel={() => { setIsPropuestasDialogVisible(false); }}
                codigo={codigo}
            />
        </>
    );
};

export default TableCliente;